import React, { useState } from "react";
import { FiCalendar, FiClock, FiX } from "react-icons/fi";

/**
 * Modal đơn giản để lên lịch đăng bài với 1 thời điểm duy nhất.
 * UI dựa trên chế độ Ngày cố định của FixedDateScheduleUI.
 */
const SchedulePostModal = ({ isOpen, onClose, onConfirm, isScheduling }) => {
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleHour, setScheduleHour] = useState("08");
  const [scheduleMinute, setScheduleMinute] = useState("00");

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!scheduleDate) {
      alert("Vui lòng chọn ngày đăng bài!");
      return;
    }

    // Validate date is in future
    const selectedDate = new Date(scheduleDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDate < today) {
      alert("Vui lòng chọn ngày trong tương lai!");
      return;
    }

    // Validate time is in future if same day
    const now = new Date();
    const todayStr = now.toISOString().split("T")[0];
    if (scheduleDate === todayStr) {
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const selectedHour = parseInt(scheduleHour);
      const selectedMinute = parseInt(scheduleMinute);

      if (
        selectedHour < currentHour ||
        (selectedHour === currentHour && selectedMinute <= currentMinute)
      ) {
        alert("Vui lòng chọn thời gian trong tương lai!");
        return;
      }
    }

    onConfirm({
      date: scheduleDate,
      time: `${scheduleHour}:${scheduleMinute}`,
    });
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("vi-VN", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-blue-600 text-white">
          <div className="flex items-center gap-2">
            <FiCalendar size={20} />
            <h3 className="font-semibold text-lg">Lên lịch đăng bài</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-blue-500 rounded-full transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5">
          {/* Date picker */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              <FiCalendar className="inline mr-2 text-blue-600" />
              Chọn ngày
            </label>
            <input
              type="date"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
            />
            {scheduleDate && (
              <p className="mt-1 text-sm text-blue-600">
                {formatDisplayDate(scheduleDate)}
              </p>
            )}
          </div>

          {/* Time picker */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              <FiClock className="inline mr-2 text-blue-600" />
              Giờ đăng bài
            </label>
            <div className="flex items-center gap-0 border border-gray-300 rounded-lg overflow-hidden shadow-sm">
              {/* Hour dropdown */}
              <div className="relative flex-1">
                <select
                  value={scheduleHour}
                  onChange={(e) => setScheduleHour(e.target.value)}
                  className="w-full px-4 py-3 text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 border-none focus:outline-none appearance-none text-center font-medium text-lg"
                >
                  {Array.from({ length: 24 }, (_, i) => {
                    const hour = i < 10 ? `0${i}` : `${i}`;
                    return (
                      <option key={hour} value={hour}>
                        {hour}
                      </option>
                    );
                  })}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                  <svg
                    className="w-4 h-4 fill-current"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>

              {/* Colon separator */}
              <div className="flex items-center justify-center bg-gray-50 px-3">
                <span className="text-gray-500 font-bold text-lg">:</span>
              </div>

              {/* Minute dropdown */}
              <div className="relative flex-1">
                <select
                  value={scheduleMinute}
                  onChange={(e) => setScheduleMinute(e.target.value)}
                  className="w-full px-4 py-3 text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 border-none focus:outline-none appearance-none text-center font-medium text-lg"
                >
                  {(() => {
                    const isDev = process.env.NODE_ENV === 'development';
                    const step = isDev ? 1 : 5;
                    const count = isDev ? 60 : 12;
                    return Array.from({ length: count }, (_, i) => {
                      const minute = i * step;
                      const formatted = minute < 10 ? `0${minute}` : `${minute}`;
                      return (
                        <option key={formatted} value={formatted}>
                          {formatted}
                        </option>
                      );
                    });
                  })()}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                  <svg
                    className="w-4 h-4 fill-current"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Info note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-700">
              💡 Bài viết sẽ được tự động đăng lên fanpage đã chọn vào thời gian
              bạn đặt lịch. Hệ thống kiểm tra mỗi 5 phút.
            </p>
          </div>
        </div>

        {/* Footer buttons */}
        <div className="flex gap-3 px-5 pb-5">
          <button
            onClick={handleConfirm}
            disabled={isScheduling || !scheduleDate}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium text-white transition-colors ${
              isScheduling || !scheduleDate
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {isScheduling ? (
              <>
                <svg
                  className="animate-spin h-5 w-5"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Đang lên lịch...
              </>
            ) : (
              <>
                <FiCalendar />
                Xác nhận lên lịch
              </>
            )}
          </button>
          <button
            onClick={onClose}
            disabled={isScheduling}
            className="flex-1 py-3 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-colors"
          >
            Hủy
          </button>
        </div>
      </div>
    </div>
  );
};

export default SchedulePostModal;
